import React, { useEffect, useState } from 'react';
import { useManualQuery } from 'graphql-hooks';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';

import { SideDrawer } from '../SideDrawer';
import { MODERATION_DECISION } from '../../graphql/report/query';
import { getTimeAgo } from '../../helpers';
import styles from './style';

const CLOUDFRONT_URL = process.env.REACT_APP_AWS_CLOUDFRONT_URL;

interface ModerationDecision {
  title: string;
  description: string;
  resolvedAt?: number;
  thumbnail?: string;
}

interface RemovalDrawerProps {
  // Report of the removed video, '' when the drawer is closed.
  reportId: string;
  onClose: () => void;
}

// Detail of the removal of a video for its creator, opened from the videoModerated notification.
// Same drawer as the report of the admin, without what only the admin sees (who reported, the actions).
const RemovalDrawer: React.FC<RemovalDrawerProps> = React.memo((props) => {
  const { reportId, onClose } = props;
  const { t, i18n } = useTranslation();
  const [moderationDecision] = useManualQuery(MODERATION_DECISION, { skipCache: true });
  const [decision, setDecision] = useState<ModerationDecision | null>(null);

  useEffect(() => {
    if (!reportId) {
      return;
    }
    setDecision(null);
    const getDecision = async () => {
      const { data } = await moderationDecision({
        variables: { reportId, language: i18n.language },
      }) as any;
      if (data && data.moderationDecision) {
        setDecision(data.moderationDecision);
      }
    };
    getDecision();
  }, [i18n.language, moderationDecision, reportId]);

  const footer = <Button onClick={onClose} sx={styles.button}>{t('admin.notifs.removalClose')}</Button>;

  return (
    <SideDrawer open={!!reportId} onClose={onClose} title={t('admin.notifs.removalTitle')} footer={footer}>
      {!decision && <CircularProgress size={20} sx={styles.spinner} />}
      {decision && (
        <>
          {decision.thumbnail && (
            <Box sx={styles.preview}>
              <Box component="img" src={`${CLOUDFRONT_URL}${decision.thumbnail}`} alt="" sx={styles.thumb} />
            </Box>
          )}
          <Box sx={styles.grid}>
            <Box sx={styles.tile}>
              <Typography sx={styles.tileKey}>{t('admin.notifs.removalStatus')}</Typography>
              <Typography sx={styles.tileValue}>{t('admin.notifs.removalRemoved')}</Typography>
            </Box>
            {!!decision.resolvedAt && (
              <Box sx={styles.tile}>
                <Typography sx={styles.tileKey}>{t('admin.notifs.removalDate')}</Typography>
                <Typography sx={styles.tileValue}>{getTimeAgo(decision.resolvedAt, t)}</Typography>
              </Box>
            )}
          </Box>
          <Box sx={styles.reasonCard}>
            <Typography sx={styles.tileKey}>{t('admin.notifs.removalReason')}</Typography>
            <Typography sx={styles.reason}>{decision.title}</Typography>
            <Typography sx={styles.description}>{decision.description}</Typography>
          </Box>
        </>
      )}
    </SideDrawer>
  );
});

export default RemovalDrawer;
